import * as XLSX from "xlsx";
import {
  filterJournals,
  profitAndLoss,
  balanceSheet,
  trialBalance,
  generalLedger,
  monthsCovered,
  journalsForMonth,
  vatSummary,
} from "./computeFromJournals";

const MONTH_LABEL = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function monthLabel(ym) {
  const [y, m] = ym.split('-');
  return MONTH_LABEL[parseInt(m, 10) - 1] + ' ' + y;
}

function dlFile(wb, name) {
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([out], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

function hdr(rows, title, subtitle) {
  rows.push([title]); rows.push([subtitle]); rows.push([]);
}

// Resolve the period label passed from the UI. `periodLabel` may be an object
// { title, file } (e.g. "YOA 2026 (…)" / "YOA 2026") or undefined, in which case
// we fall back to the raw date range.
function resolvePeriod(from, to, periodLabel) {
  const range = (from || '?') + ' to ' + (to || '?');
  if (periodLabel && periodLabel.title) return { title: periodLabel.title, file: periodLabel.file || range };
  return { title: range, file: range };
}

// ─── P&L (single period) ──────────────────────────────────────────────────────
function mkPnL(coa, journals, name, period) {
  const p = profitAndLoss(coa, journals);
  const rows = [];
  hdr(rows, name, "Profit and Loss - " + period);
  rows.push(["Code", "Account Name", "Amount (R)"]);
  rows.push(["", "Revenue", ""]);
  p.sections.revenue.forEach((e) => rows.push([e.code, e.name, e.amount]));
  rows.push(["", "Total Revenue", p.rev]);
  rows.push([]);
  rows.push(["", "Less: Cost of Revenue", ""]);
  p.sections.cogs.forEach((e) => rows.push([e.code, e.name, e.amount]));
  rows.push(["", "Total Cost of Revenue", p.cogs]);
  rows.push([]);
  rows.push(["", "Gross Profit", p.gp]);
  rows.push([]);
  rows.push(["", "Less: Operating Expenses", ""]);
  p.sections.expense.forEach((e) => rows.push([e.code, e.name, e.amount]));
  rows.push(["", "Total Operating Expenses", p.opex]);
  rows.push([]);
  rows.push(["", "Operating Income (or Loss)", p.opInc]);
  rows.push([]);
  if (p.sections.other_income.length) {
    rows.push(["", "Plus: Other Income", ""]);
    p.sections.other_income.forEach((e) => rows.push([e.code, e.name, e.amount]));
    rows.push(["", "Total Other Income", p.oi]);
    rows.push([]);
  }
  if (p.sections.other_expense.length) {
    rows.push(["", "Less: Other Expenses", ""]);
    p.sections.other_expense.forEach((e) => rows.push([e.code, e.name, e.amount]));
    rows.push(["", "Total Other Expenses", p.oe]);
    rows.push([]);
  }
  rows.push(["", "Net Profit / (Loss)", p.np]);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 12 }, { wch: 45 }, { wch: 18 }];
  return ws;
}

// ─── Balance Sheet (single point in time) ─────────────────────────────────────
function mkBS(coa, opening, journals, name, period) {
  const bs = balanceSheet(coa, opening, journals);
  const rows = [];
  hdr(rows, name, "Balance Sheet - As at " + period);
  rows.push(["Code", "Account", "Amount (R)"]);
  rows.push(["", "ASSETS", ""]);
  bs.assets.forEach((e) => rows.push([e.code, e.name, e.amount]));
  rows.push(["", "TOTAL ASSETS", bs.totalAssets]);
  rows.push([]);
  rows.push(["", "LIABILITIES", ""]);
  bs.liabilities.forEach((e) => rows.push([e.code, e.name, e.amount]));
  rows.push(["", "TOTAL LIABILITIES", bs.totalLiabilities]);
  rows.push([]);
  rows.push(["", "EQUITY", ""]);
  bs.equity.forEach((e) => rows.push([e.code, e.name, e.amount]));
  rows.push(["", "TOTAL EQUITY", bs.totalEquity]);
  rows.push([]);
  rows.push(["", "TOTAL LIABILITIES + EQUITY", bs.totalLiabilities + bs.totalEquity]);
  rows.push(["", "Balancing Difference (should be 0)", bs.diff]);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 12 }, { wch: 45 }, { wch: 18 }];
  return ws;
}

// ─── Trial Balance ────────────────────────────────────────────────────────────
function mkTB(coa, opening, journals, name, period) {
  const tb = trialBalance(coa, opening, journals);
  const rows = [];
  hdr(rows, name, "Trial Balance - " + period);
  rows.push(["Code", "Account", "Type", "Debit (R)", "Credit (R)"]);
  tb.rows.forEach((r) => rows.push([r.code, r.name, r.type, r.dr || "", r.cr || ""]));
  rows.push([]);
  rows.push(["", "TOTALS", "", tb.totalDr, tb.totalCr]);
  rows.push(["", "Difference (should be 0)", "", tb.diff, ""]);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 10 }, { wch: 40 }, { wch: 12 }, { wch: 16 }, { wch: 16 }];
  return ws;
}

// ─── General Ledger ───────────────────────────────────────────────────────────
function mkGL(coa, opening, journals, name, period) {
  const gl = generalLedger(coa, opening, journals);
  const rows = [];
  hdr(rows, name, "General Ledger - " + period);
  rows.push(["Date", "Journal Narration", "Source Description", "Debit (R)", "Credit (R)", "Running Balance"]);
  gl.forEach((g) => {
    rows.push([]);
    rows.push([g.code + " — " + g.name + " (" + g.type + ")", "", "", "", "", ""]);
    g.lines.forEach((l) => rows.push([l.date, l.desc, l.narration, l.dr || "", l.cr || "", l.running]));
    rows.push(["", "Closing", "", "", "", g.closing]);
  });
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 12 }, { wch: 40 }, { wch: 40 }, { wch: 14 }, { wch: 14 }, { wch: 16 }];
  return ws;
}

// ─── Journals listing (chronological) ─────────────────────────────────────────
function mkJournals(journals, name, period) {
  const rows = [];
  hdr(rows, name, "Journal Entries - " + period);
  rows.push(["Date", "Narration", "Account Code", "Account Name", "Debit (R)", "Credit (R)", "Source"]);
  (journals || []).forEach((j) => {
    (j.lines || []).forEach((l, i) => {
      rows.push([
        i === 0 ? j.date : "",
        i === 0 ? j.narration : "",
        l.accountCode,
        l.accountName || "",
        Number(l.dr) || "",
        Number(l.cr) || "",
        i === 0 && j.sourceRow ? j.sourceRow.description : "",
      ]);
    });
    rows.push([]);
  });
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 12 }, { wch: 36 }, { wch: 12 }, { wch: 36 }, { wch: 14 }, { wch: 14 }, { wch: 36 }];
  return ws;
}

// ─── VAT201 summary ───────────────────────────────────────────────────────────
function mkVAT201(coa, journals, name, period, ratePct) {
  const v = vatSummary(coa, journals, ratePct);
  const rows = [];
  hdr(rows, name, "VAT201 Summary - " + period + " (VAT @ " + v.rate + "%)");
  rows.push(["", "Output VAT - standard-rated sales (incl. " + v.standardSales.toFixed(2) + ")", v.totalOutput]);
  rows.push(["", "Input VAT - deductible purchases (incl. " + v.inputPurchases.toFixed(2) + ")", v.totalInput]);
  rows.push(["", v.net >= 0 ? "Net VAT payable to SARS" : "Net VAT refundable", v.net]);
  rows.push([]);
  rows.push(["Output VAT (sales)", "Incl.", "VAT"]);
  v.output.forEach((e) => rows.push([e.code + " " + e.name, e.amount, e.vat]));
  rows.push([]);
  rows.push(["Input VAT (purchases)", "Incl.", "VAT"]);
  v.input.forEach((e) => rows.push([e.code + " " + e.name, e.amount, e.vat]));
  if (v.excluded.length) {
    rows.push([]);
    rows.push(["Excluded / denied", "Amount", "Reason"]);
    v.excluded.forEach((e) => rows.push([e.code + " " + e.name, e.amount, e.reason]));
  }
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 48 }, { wch: 18 }, { wch: 18 }];
  return ws;
}

export function exportVAT201(coa, journals, projectName, from, to, ratePct, periodLabel) {
  const filtered = filterJournals(journals, from, to);
  const pr = resolvePeriod(from, to, periodLabel);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, mkVAT201(coa, filtered, projectName, pr.title, ratePct), "VAT201");
  dlFile(wb, safeName(projectName) + ' - VAT201 ' + pr.file + '.xlsx');
}

// ─── BobCorp-style monthly P&L with cumulative tab ────────────────────────────
function mkPnLSheetBobCorp(coa, journals, label) {
  const p = profitAndLoss(coa, journals);
  const rows = [[, , label]];
  rows.push([]);
  rows.push(["Code", "Account Name", "Balance"]);
  rows.push(["", "Revenue", ""]);
  p.sections.revenue.forEach((e) => rows.push([e.code, e.name, e.amount]));
  rows.push(["", "Total Revenue", p.rev]);
  rows.push(["", "Less Costs of Revenue", p.cogs]);
  rows.push([]);
  rows.push(["", "Gross Profit", p.gp]);
  rows.push(["", "Less Operating Expenses", ""]);
  p.sections.expense.forEach((e) => rows.push([e.code, e.name, e.amount]));
  rows.push(["", "Total Less Operating Expenses", p.opex]);
  rows.push([]);
  rows.push(["", "Operating Income (or Loss)", p.opInc]);
  rows.push(["", "Plus Other Income", p.oi]);
  rows.push(["", "Less Other Expenses", p.oe]);
  rows.push([]);
  rows.push(["", "Net Profit", p.np]);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 13 }, { wch: 50 }, { wch: 15 }];
  return ws;
}

function safeName(s) {
  return s.replace(/[^a-zA-Z0-9 ]/g, "").substring(0, 25).trim();
}

// Build a multi-tab workbook in the BobCorp shape: one P&L per month + cumulative tab.
export function exportBobCorpStyle(coa, journals, projectName, from, to, periodLabel) {
  const filtered = filterJournals(journals, from, to);
  const pr = resolvePeriod(from, to, periodLabel);
  const months = monthsCovered(filtered);
  const wb = XLSX.utils.book_new();
  months.forEach((ym) => {
    const monthJournals = journalsForMonth(filtered, ym);
    XLSX.utils.book_append_sheet(wb, mkPnLSheetBobCorp(coa, monthJournals, monthLabel(ym)), monthLabel(ym).substring(0, 31));
  });
  const cumulativeLabel = (periodLabel && periodLabel.title) ? ('Cumulative — ' + pr.title) : ('From ' + (from || (months[0] ? months[0] + '-01' : '')) + ' to ' + (to || ''));
  XLSX.utils.book_append_sheet(
    wb,
    mkPnLSheetBobCorp(coa, filtered, cumulativeLabel),
    'Cumulative'
  );
  dlFile(wb, safeName(projectName) + ' P&L by Month ' + pr.file + '.xlsx');
}

// Single-period workbook with all statements as separate tabs.
export function exportFullWorkbook(coa, opening, journals, projectName, from, to, periodLabel) {
  const filtered = filterJournals(journals, from, to);
  const pr = resolvePeriod(from, to, periodLabel);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, mkPnL(coa, filtered, projectName, pr.title), "Profit and Loss");
  XLSX.utils.book_append_sheet(wb, mkBS(coa, opening, filtered, projectName, pr.title), "Balance Sheet");
  XLSX.utils.book_append_sheet(wb, mkTB(coa, opening, filtered, projectName, pr.title), "Trial Balance");
  XLSX.utils.book_append_sheet(wb, mkGL(coa, opening, filtered, projectName, pr.title), "General Ledger");
  XLSX.utils.book_append_sheet(wb, mkJournals(filtered, projectName, pr.title), "Journals");
  dlFile(wb, safeName(projectName) + " Financials " + pr.file + ".xlsx");
}

// Individual exports.
export function exportSingle(type, coa, opening, journals, projectName, from, to, periodLabel) {
  const filtered = filterJournals(journals, from, to);
  const pr = resolvePeriod(from, to, periodLabel);
  const period = pr.title;
  const wb = XLSX.utils.book_new();
  let label;
  switch (type) {
    case 'pnl': XLSX.utils.book_append_sheet(wb, mkPnL(coa, filtered, projectName, period), "Profit and Loss"); label = 'Profit and Loss'; break;
    case 'bs': XLSX.utils.book_append_sheet(wb, mkBS(coa, opening, filtered, projectName, period), "Balance Sheet"); label = 'Balance Sheet'; break;
    case 'tb': XLSX.utils.book_append_sheet(wb, mkTB(coa, opening, filtered, projectName, period), "Trial Balance"); label = 'Trial Balance'; break;
    case 'gl': XLSX.utils.book_append_sheet(wb, mkGL(coa, opening, filtered, projectName, period), "General Ledger"); label = 'General Ledger'; break;
    case 'journals': XLSX.utils.book_append_sheet(wb, mkJournals(filtered, projectName, period), "Journals"); label = 'Journals'; break;
    default: return;
  }
  dlFile(wb, safeName(projectName) + ' - ' + label + ' ' + pr.file + '.xlsx');
}
